import type { GlbHierarchyNode } from '../../imported-model/loaders/glb';
import { AssemblyComponentFixationService } from '../component-fixation/AssemblyComponentFixationService';
import {
  AssemblyReferenceCopyLineageRole,
  type AssemblyReferenceCopyLineageMetadata,
} from './AssemblyReferenceCopyTypes';

/** 装配体引用复制名称后缀格式。 */
const ASSEMBLY_REFERENCE_COPY_NAME_SUFFIX_PATTERN = /_(\d+)$/;

/**
 * 装配体引用复制命名输入。
 */
export interface AssemblyReferenceCopyNameResolutionInput {
  /** 当前复制体要插入的父级 hierarchy 节点。 */
  readonly parentHierarchy: GlbHierarchyNode;
  /** 当前被复制的源 hierarchy 节点。 */
  readonly sourceHierarchy: GlbHierarchyNode;
  /** 当前被复制对象的复制族元数据。 */
  readonly sourceLineage?: AssemblyReferenceCopyLineageMetadata | null;
  /** 源对象运行时名称兜底值。 */
  readonly fallbackSourceName?: string;
  /** 当源名称缺失时使用的兜底基础名。 */
  readonly defaultBaseName: string;
}

/**
 * 装配体引用复制命名服务。
 *
 * 设计说明：
 * 1. 装配体复制创建的是新的 occurrence，显示名必须表达同源复制序号；
 * 2. A 复制后应得到 A_1，继续复制 A 或 A_1 都应得到 A_2；
 * 3. 命名规则放在 cad-core，保存、导入、历史回放与 web-client 共享同一语义入口。
 */
export class AssemblyReferenceCopyNameService {
  /**
   * 解析下一个复制体显示名称。
   *
   * @param input 命名上下文。
   * @returns 不与同级节点冲突的复制体显示名称。
   */
  public static resolveNextDisplayName(input: AssemblyReferenceCopyNameResolutionInput): string {
    const existingNames = this.collectDirectChildNames(input.parentHierarchy);
    const baseName = this.resolveFamilyBaseName(input);
    let suffix = 1;

    // 装配复制必须始终生成新的 occurrence 名称，不能复用原始名称本身。
    while (existingNames.has(this.createCopyName(baseName, suffix))) {
      suffix += 1;
    }

    return this.createCopyName(baseName, suffix);
  }

  /**
   * 收集父节点下所有直接子节点名称。
   *
   * @param parentHierarchy 当前复制体父级 hierarchy。
   * @returns 已占用名称集合。
   */
  private static collectDirectChildNames(parentHierarchy: GlbHierarchyNode): Set<string> {
    const existingNames = new Set<string>();
    for (const child of parentHierarchy.children) {
      const childName = this.normalizeName(child.name);
      if (childName) {
        existingNames.add(childName);
      }
    }
    return existingNames;
  }

  /**
   * 解析复制族基础名称。
   *
   * @param input 命名上下文。
   * @returns 去除复制序号后的基础名称。
   */
  private static resolveFamilyBaseName(input: AssemblyReferenceCopyNameResolutionInput): string {
    const sourceLineage = input.sourceLineage ?? null;
    const sourceName = this.resolveSourceDisplayName(input);

    if (!sourceLineage) {
      return sourceName;
    }

    const originName = sourceLineage.role === AssemblyReferenceCopyLineageRole.ReferenceOccurrence
      ? this.resolveOriginDisplayName(input.parentHierarchy, sourceLineage.originOccurrenceId)
      : null;
    if (originName) {
      return originName;
    }

    // 源本身已经是复制体但原始 occurrence 可能已删除，此时从 A_1 回退为 A。
    return this.stripCopySuffix(sourceName);
  }

  /**
   * 解析源对象显示名称。
   *
   * @param input 命名上下文。
   * @returns 源对象名称。
   */
  private static resolveSourceDisplayName(input: AssemblyReferenceCopyNameResolutionInput): string {
    return this.normalizeName(input.sourceHierarchy.name)
      || this.normalizeName(input.fallbackSourceName)
      || this.normalizeName(input.defaultBaseName)
      || '复制零部件';
  }

  /**
   * 解析原始 occurrence 的显示名称。
   *
   * @param rootHierarchy 当前装配 hierarchy 根。
   * @param originOccurrenceId 原始 occurrence ID。
   * @returns 原始 occurrence 名称；找不到时返回 null。
   */
  private static resolveOriginDisplayName(
    rootHierarchy: GlbHierarchyNode,
    originOccurrenceId: string,
  ): string | null {
    const originHierarchy = this.findHierarchyNodeByObjectId(rootHierarchy, originOccurrenceId);
    return originHierarchy ? this.stripCopySuffix(this.normalizeName(originHierarchy.name)) : null;
  }

  /**
   * 按 ObjectId 在 hierarchy 树中查找节点。
   *
   * @param node 当前遍历节点。
   * @param objectId 目标 ObjectId。
   * @returns 命中的 hierarchy 节点；找不到时返回 null。
   */
  private static findHierarchyNodeByObjectId(node: GlbHierarchyNode, objectId: string): GlbHierarchyNode | null {
    if (node.objectId === objectId) {
      return node;
    }

    for (const child of node.children) {
      const matchedNode = this.findHierarchyNodeByObjectId(child, objectId);
      if (matchedNode) {
        return matchedNode;
      }
    }

    return null;
  }

  /**
   * 创建带复制序号的名称。
   *
   * @param baseName 复制族基础名称。
   * @param suffix 复制序号。
   * @returns 复制体显示名称。
   */
  private static createCopyName(baseName: string, suffix: number): string {
    return `${baseName}_${suffix}`;
  }

  /**
   * 移除复制体名称后缀。
   *
   * @param name 待处理名称。
   * @returns 去除 `_数字` 后缀的名称。
   */
  private static stripCopySuffix(name: string): string {
    const match = ASSEMBLY_REFERENCE_COPY_NAME_SUFFIX_PATTERN.exec(name);
    if (!match || match.index <= 0) {
      return name;
    }

    return name.slice(0, match.index).trim() || name;
  }

  /**
   * 标准化显示名称。
   *
   * @param name 待标准化名称。
   * @returns 去除首尾空白后的名称。
   */
  private static normalizeName(name: string | null | undefined): string {
    return typeof name === 'string'
      ? AssemblyComponentFixationService.trimComponentTreeStatusPrefix(name)
      : '';
  }
}
